import React from 'react';
import PropTypes from 'prop-types';
/* helpers */
import {getClassName} from '../../../../helpers/dom-helpers';
/* styles */
import './input.scss';

class Input extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      value: this.props.value,
      isFocused: false,
    };
  }

  static getDerivedStateFromProps(props, state) {
    if (props.value !== state.prevValue) {
      return {
        prevValue: props.value,
        value: props.value,
      };
    }
    return null;
  }

  componentDidMount() {
    if (this.props.autoFocus) {
      this.autoFocusTimeout = setTimeout(() => {
        this.input.focus();
      }, this.props.autoFocusDelay);
    }
  }

  componentWillUnmount() {
    if (this.autoFocusTimeout) {
      clearTimeout(this.autoFocusTimeout);
    }
    if (this.changeDebounce) {
      clearTimeout(this.changeDebounce);
    }
  }

  onOuterDivBlur = () => {
    this.setState({isFocused: false});
  };

  onInput = (event) => {
    if (this.inputTimeout) {
      clearTimeout(this.inputTimeout);
    }
    const value = event.target.value;
    this.inputTimeout = setTimeout(() => this.onChange(event, value), 0);
  };

  onChange = (event, value) => {
    if (this.inputTimeout) {
      clearTimeout(this.inputTimeout);
    }
    this.setState({value: value !== undefined ? value : event.target.value}, () => {
      if (this.changeDebounce) {
        clearTimeout(this.changeDebounce);
      }
      this.changeDebounce = setTimeout(() => {
        this.props.onChange(this.state.value.trim());
      }, this.props.changeDebounce);
    });
  };

  onFocusChange = (isFocused) => {
    if (this.props.onFocusChange) {
      this.props.onFocusChange(isFocused);
    }
    if (isFocused !== this.state.isFocused) {
      this.setState({isFocused});
    }
  };

  setFocus = () => {
    this.input.focus();
  };

  clearValue = (e) => {
    if (this.props.onClear) {
      this.props.onClear();
    } else {
      e.stopPropagation();
      if (!this.props.isDisabled && this.props.onChange) {
        this.props.onChange('');
      }
    }
  };

  getIconElement = (iconClass) => <span className={`icon glu-input__icon ${iconClass}`} />;

  render() {
    const className = getClassName('glu-input', this.props.className, {
      'glu-input--disabled': this.props.isDisabled,
      'glu-input--focused': !this.props.isDisabled && this.state.isFocused,
      'glu-input--warning': this.props.invalid && this.props.warning,
      'glu-input--invalid': this.props.invalid && !this.props.warning,
    });

    const clearIconClass = getClassName('icon glu-input__icon glu-input__icon--clear icon-close', {
      'glu-input__icon--clickable': !this.props.isDisabled && this.props.onChange,
    });

    let inputValue =
      typeof this.state.value === 'number' || typeof this.state.value === 'string'
        ? this.state.value
        : '';

    if (this.props.readOnly && this.props.displayValue) {
      inputValue = this.props.displayValue;
    }
    const suffixClickHandler =
      this.props.onSuffixClick && !this.props.isDisabled ? this.props.onSuffixClick : this.setFocus;

    const suffix = this.props.suffixIcon
      ? this.getIconElement(this.props.suffixIcon)
      : this.props.suffix;
    return (
      <div
        className={className}
        title={this.props.title}
        onClick={!this.props.isDisabled ? this.props.onClick : undefined}
        onBlur={this.onOuterDivBlur}
      >
        <input
          key="input"
          onInput={this.onInput}
          autoComplete={this.props.autoComplete}
          readOnly={this.props.readOnly}
          ref={(input) => {
            this.input = input;
          }}
          type={this.props.type}
          className="glu-input__input"
          name={this.props.name || this.props.id}
          id={this.props.id}
          value={inputValue}
          placeholder={this.props.placeholder}
          onChange={this.onChange}
          onFocus={this.onFocusChange.bind(this, true)}
          onBlur={this.onFocusChange.bind(this, false)}
          disabled={this.props.isDisabled}
          onKeyDown={this.props.onKeyDown}
        />
        {this.props.clearable && (this.props.value || this.props.onClear) && (
          <div className="glu-input__item glu-input__item--clickable" onClick={this.clearValue}>
            <span key="clearable" className={clearIconClass} />
          </div>
        )}
        {suffix && (
          <div
            key="suffix"
            className={`glu-input__item${
              this.props.onSuffixClick ? ' glu-input__item--clickable' : ''
            }`}
            onClick={suffixClickHandler}
          >
            {suffix}
          </div>
        )}
      </div>
    );
  }
}

Input.propTypes = {
  autoComplete: PropTypes.string,
  autoFocus: PropTypes.bool,
  autoFocusDelay: PropTypes.number,
  changeDebounce: PropTypes.number,
  className: PropTypes.string,
  clearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  displayValue: PropTypes.string,
  id: PropTypes.string,
  invalid: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  onClick: PropTypes.func,
  onFocusChange: PropTypes.func,
  onSuffixClick: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  suffix: PropTypes.node,
  suffixIcon: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  warning: PropTypes.bool,
  title: PropTypes.string,
};

Input.defaultProps = {
  autoComplete: undefined,
  autoFocus: false,
  autoFocusDelay: 500,
  changeDebounce: 50,
  className: '',
  clearable: false,
  isDisabled: false,
  displayValue: undefined,
  id: undefined,
  invalid: false,
  name: '',
  onChange: undefined,
  onClear: undefined,
  onClick: undefined,
  onFocusChange: undefined,
  onSuffixClick: undefined,
  placeholder: '',
  readOnly: false,
  suffix: '',
  suffixIcon: '',
  type: 'text',
  value: '',
  warning: false,
  title: '',
};

export default Input;
